import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getUser } from "../../REDUX/Models/users.Model";
import { getSkills } from "../../REDUX/Models/skills.Model";
import { getUserCurriculums } from "../../REDUX/Models/curriculums.Model";
import {
  addFriend,
  getFriends,
  checkFriends,
} from "../../REDUX/Models/friends.Model";
import { getUserCertificates } from "../../REDUX/Models/certificates.Model";
import { createNotifi } from "../../REDUX/Models/notifi.Model";
import { signOut } from "../../REDUX/Slices/users.Slice";
import avatar from "../../assets/icons/avatar.svg";
import Curriculums from "./Curriculums";
import Friends from "./Friends";
import Skills from "./Skills";
import Certificate from "../../Components/Certificate";
import "./index.scss";

export default function Profile() {
  const { id: profileId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { userData, isSigned } = useSelector((state) => state);

  const [user, setUser] = useState({});
  const [friend, setFriend] = useState({});
  const [skills, setSkills] = useState([]);
  const [friends, setFriends] = useState([]);
  const [curriculums, setCurriculums] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [disableButton, setDisableButton] = useState(false);
  const [buttonText, setButtonText] = useState("");

  const [activeTab, setActiveTab] = useState("curriculums");

  const fetchData = async () => {
    try {
      const userRes = await getUser(profileId);
      const currentUser = userRes.data?.user;
      if (!currentUser) return navigate("/");

      setUser(currentUser);
      document.title = `طورنى - ${currentUser.first_name} ${currentUser.last_name}`;

      const [skillsRes, curriculumsRes, certRes, friendsRes] =
        await Promise.all([
          getSkills(profileId),
          getUserCurriculums(profileId),
          getUserCertificates(profileId),
          getFriends(profileId),
        ]);

      setSkills(skillsRes.data?.skills || []);
      setCurriculums(curriculumsRes.data?.curriculums || []);
      setCertificates(certRes.data?.certificates || []);
      setFriends(friendsRes.data?.friends || []);

      const friendCheck = await checkFriends(profileId, {
        user_id: userData.id,
      });
      setFriend(friendCheck.data?.friend || {});
    } catch (err) {
      console.error("Error loading profile:", err);
      navigate("/");
    }
  };

  const handleFriendRequest = async () => {
    try {
      await addFriend(profileId, { user_id: userData.id });

      await createNotifi({
        text: `ارسل ${userData.first_name} ${userData.last_name} طلب صداقة اليك.`,
        user_id: profileId,
      });

      await createNotifi({
        text: `لقد اصبحت صديقا مع ${user.first_name} ${user.last_name}`,
        user_id: userData.id,
      });

      setDisableButton(true);
      setButtonText("تم الإرسال");
    } catch (err) {
      console.error("Friend request error:", err);
      dispatch(signOut());
    }
  };

  useEffect(() => {
    if (!isSigned || !userData?.id) {
      navigate(`/profile/view/${profileId}`);
    } else {
      fetchData();
    }
  }, [profileId, isSigned]);

  useEffect(() => {
    if (userData.id === user.id) {
      setButtonText("صفحتي");
      setDisableButton(true);
    } else if (friend?.user_id_to === profileId) {
      setButtonText("أصدقاء");
      setDisableButton(true);
    } else {
      setButtonText("إرسال طلب صداقة");
    }
  }, [userData.id, user.id, friend]);

  return (
    <div className="profile">
      <div className="container">
        <h2 className="header">الصفحة الشخصية</h2>
        <div className="row">
          <div className="profile-img">
            <img src={avatar} alt="Profile" />
          </div>
          <div className="profile-info">
            <div className="info">
              <div className="info-header">
                <h2>
                  {user.first_name} {user.last_name}
                </h2>
                <button onClick={handleFriendRequest} disabled={disableButton}>
                  {buttonText}
                </button>
              </div>
              <i className="fa-light fa-at"></i>
              <p>{user.user_name}</p>
            </div>
            <div className="location">
              <i className="fa-light fa-location-dot"></i>
              <p>
                {user.location ? user.location : <span>لا يوجد مدينة.</span>}
              </p>
            </div>
            <div className="rating">
              <i className="fa-solid fa-star"></i>
              <p>
                {user.rating ? (
                  <span>{`${user.rating} نجوم`}</span>
                ) : (
                  <span>لا يوجد تقيم.</span>
                )}
              </p>
            </div>
            <div className="bio">
              <i className="fa-solid fa-hashtag"></i>
              <p>{user.bio ? user.bio : <span>لا يوجد نبذة تعريفية.</span>}</p>
            </div>
          </div>
        </div>

        <div className="profile-content">
          <div className="toggle">
            {["curriculums", "skills", "certificates", "friends"].map((tab) => (
              <p
                key={tab}
                className={activeTab === tab ? "active" : ""}
                onClick={() => setActiveTab(tab)}
              >
                {tab === "curriculums" && (
                  <>
                    المناهج <i className="fa-solid fa-graduation-cap"></i>
                  </>
                )}
                {tab === "skills" && (
                  <>
                    المهارات <i className="fa-solid fa-shapes"></i>
                  </>
                )}
                {tab === "certificates" && (
                  <>
                    الشهادات <i className="fa-solid fa-file-certificate"></i>
                  </>
                )}
                {tab === "friends" && (
                  <>
                    الأصدقاء <i className="fa-solid fa-user-group"></i>
                  </>
                )}
              </p>
            ))}
          </div>

          <div className="component">
            {activeTab === "curriculums" && (
              <div className="profile-curriculums">
                {curriculums.length > 0 ? (
                  curriculums.map((c, i) => (
                    <Curriculums key={i} curriculum={c} />
                  ))
                ) : (
                  <span className="noData">لا يوجد مناهج للعرض.</span>
                )}
              </div>
            )}
            {activeTab === "skills" && (
              <div className="profile-skills">
                {skills.length > 0 ? (
                  skills.map((s, i) => <Skills key={i} skill={s} />)
                ) : (
                  <span className="noData">لا يوجد مهارات للعرض.</span>
                )}
              </div>
            )}
            {activeTab === "certificates" && (
              <div className="profile-certificates">
                {certificates.length > 0 ? (
                  certificates.map((cert, i) => (
                    <Link
                      key={i}
                      to={`/verify/${cert.cer_id}`}
                      className="certificate-link"
                    >
                      <Certificate certificateProp={cert} />
                    </Link>
                  ))
                ) : (
                  <span className="noData">لا يوجد شهادات للعرض.</span>
                )}
              </div>
            )}
            {activeTab === "friends" && (
              <div className="profile-friends">
                {friends.length > 0 ? (
                  friends.map((f, i) => <Friends key={i} friend={f} />)
                ) : (
                  <span className="noData">لا يوجد أصدقاء للعرض.</span>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="user-contact">
          <h2>معلومات التواصل</h2>
          <div className="contact-info">
            {user.facebook && (
              <Link to={user.facebook} target="_blank">
                <div className="social-box">
                  <p>Facebook</p>
                  <i className="fa-brands fa-facebook-f"></i>
                </div>
              </Link>
            )}
            {user.twitter && (
              <Link to={user.twitter} target="_blank">
                <div className="social-box">
                  <p>Twitter</p>
                  <i className="fa-brands fa-twitter"></i>
                </div>
              </Link>
            )}
            {user.whatsapp && (
              <Link to={`https://wa.me/${user.whatsapp}`} target="_blank">
                <div className="social-box">
                  <p>Whatsapp</p>
                  <i className="fa-brands fa-whatsapp"></i>
                </div>
              </Link>
            )}
            {!user.facebook && !user.twitter && !user.whatsapp && (
              <p>لا يوجد معلومات للتواصل.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
